import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import BackButton from '../components/BackButton';
import { getAllValidUnlocks, UNLOCK_TTL_MS, StoredUnlock } from '../utils/unlockAccess';
import SafetyDisclaimer from '../components/SafetyDisclaimer';

type Contact = { google_maps_link: string; whatsapp: string; second_whatsapp?: string | null };

type UnlockedItem = StoredUnlock & { contact: Contact | null };

const tableMeta: Record<string, { label: string; icon: string; route: string }> = {
  rentals: { label: 'House for Rent', icon: '🔑', route: '/houses-for-rent' },
  land: { label: 'Land', icon: '🌍', route: '/land' },
  houses_for_sale: { label: 'House for Sale', icon: '🏠', route: '/houses-for-sale' },
  cars: { label: 'Car', icon: '🚗', route: '/cars' },
};

const formatTimeLeft = (verifiedAt?: number) => {
  if (!verifiedAt) return '';
  const msLeft = verifiedAt + UNLOCK_TTL_MS - Date.now();
  if (msLeft <= 0) return 'Expired';
  const hours = Math.floor(msLeft / (60 * 60 * 1000));
  const mins = Math.floor((msLeft % (60 * 60 * 1000)) / (60 * 1000));
  if (hours > 0) return `${hours}h ${mins}m left`;
  return `${mins}m left`;
};

const MyUnlocksPage = () => {
  const [items, setItems] = useState<UnlockedItem[] | null>(null);
  const [, forceTick] = useState(0);

  useEffect(() => {
    const load = async () => {
      const stored = getAllValidUnlocks();
      if (stored.length === 0) {
        setItems([]);
        return;
      }
      const withContacts = await Promise.all(
        stored.map(async (u) => {
          const rpcName = (u.listingTable === 'houses_for_sale' || u.listingTable === 'cars') ? 'get_unlocked_contact_extra' : 'get_unlocked_contact';
          const { data } = await supabase.rpc(rpcName, { ref: u.ref });
          return { ...u, contact: data && data.length > 0 ? data[0] : null };
        })
      );
      setItems(withContacts.filter(i => i.contact));
    };
    load();
  }, []);

  // Keep the "Xh Ym left" labels ticking without needing a full reload.
  useEffect(() => {
    const interval = setInterval(() => forceTick(t => t + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '32px 20px 80px' }}>
      <BackButton />
      <h1 style={{ color: 'var(--gold)', fontSize: '24px', marginTop: '16px', marginBottom: '4px' }}>My Unlocked Listings</h1>
      <p style={{ color: 'var(--text-muted)', fontSize: '13px', marginBottom: '28px' }}>
        Listings you've paid to unlock on this device, for the 24 hours after unlocking. This only works on the same phone/browser you paid from.
      </p>

      {items === null && (
        <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '40px 0' }}>Loading...</p>
      )}

      {items !== null && items.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 20px', background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: '8px' }}>
          <p style={{ fontSize: '32px', marginBottom: '12px' }}>🔒</p>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '20px' }}>
            You haven't unlocked any listings on this device yet.
          </p>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/land" className="btn-ghost" style={{ textDecoration: 'none', padding: '10px 18px', fontSize: '13px' }}>Browse Land</Link>
            <Link to="/houses-for-rent" className="btn-ghost" style={{ textDecoration: 'none', padding: '10px 18px', fontSize: '13px' }}>Browse Rentals</Link>
          </div>
        </div>
      )}

      {items !== null && items.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <SafetyDisclaimer />
          {items.map((item) => {
            const meta = tableMeta[item.listingTable] || { label: item.listingTable, icon: '📍', route: '/' };
            return (
              <div key={`${item.listingTable}-${item.listingId}`}
                style={{ background: 'var(--dark-3)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: '8px', padding: '18px 20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                  <div>
                    <span style={{ color: 'var(--text-muted)', fontSize: '11px', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                      {meta.icon} {meta.label}
                    </span>
                    <h3 style={{ color: 'var(--text)', fontSize: '16px', marginTop: '4px' }}>{item.listingTitle}</h3>
                  </div>
                  <span style={{ color: 'var(--gold)', fontSize: '11px', whiteSpace: 'nowrap' }}>
                    ⏳ {formatTimeLeft(item.verifiedAt)}
                  </span>
                </div>

                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {item.contact?.whatsapp && (
                    <a href={`https://wa.me/${item.contact.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer"
                      className="btn-primary" style={{ textDecoration: 'none', padding: '8px 16px', fontSize: '13px' }}>
                      💬 Chat
                    </a>
                  )}
                  {item.contact?.second_whatsapp && (
                    <a href={`https://wa.me/${item.contact.second_whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer"
                      className="btn-ghost" style={{ textDecoration: 'none', padding: '8px 16px', fontSize: '13px' }}>
                      💬 Alt Contact
                    </a>
                  )}
                  {item.contact?.google_maps_link && (
                    <a href={item.contact.google_maps_link} target="_blank" rel="noopener noreferrer"
                      className="btn-ghost" style={{ textDecoration: 'none', padding: '8px 16px', fontSize: '13px' }}>
                      📍 Location
                    </a>
                  )}
                  <Link to={`${meta.route}?listing=${item.listingId}`}
                    style={{ padding: '8px 16px', fontSize: '13px', color: 'var(--text-muted)', textDecoration: 'underline' }}>
                    View Listing
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyUnlocksPage;
